import { ButtonV2, Checkbox, Input, InputV2 } from '@cimplico/components';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toastNotification } from '../../../../../components/toast/Toast';
import { Entity, EntityTypeEnum } from '../../../../../types/entity';
import { useIsActiveSubscription } from '../../../../../hooks/use-is-active-subscription';
import { useUpdateEntities } from '../../../../../queries/use-update-entities';
import { useState } from 'react';
import SettingsContainer from '../../../../../components/settings-container/SettingsContainer';
import SectionLabel from '../../../../../components/section-label/SectionLabel';
import EntityTypeSelect from '../../../../../components/entity-type-select/EntityTypeSelect';
import DaySelect from '../../../../../components/day-select/DaySelect';
import MonthSelect from '../../../../../components/month-select/MonthSelect';
import GroupSelect from '../../../../../components/group-select/GroupSelect';
import EditEntityConfirm from '../EditEntityConfirm';

type EditEntityFormFields = {
    name: string;
    legalName: string;
    type: EntityTypeEnum;
    month: number | null;
    day: number | null;
    group?: string;
};

type EntityDetailsProps = { entity: Entity };

function EntityDetails({ entity }: EntityDetailsProps): JSX.Element {
    const [diffLegalName, setDiffLegalName] = useState(entity.name !== entity.legalName);
    const [showEntityTypeConfirm, setShowEntityTypeConfirm] = useState(false);

    const activeSubscription = useIsActiveSubscription();
    const updateEntitiesQuery = useUpdateEntities();

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit,
        getValues,
        setValue,
        setError,
    } = useForm<EditEntityFormFields>({
        defaultValues: {
            name: entity.name,
            legalName: entity.legalName,
            type: entity.type,
            month: entity.financialYearEndMonth,
            day: entity.financialYearEndDay,
            group: entity.groups[0]?.id,
        },
    });

    const name = useWatch({ control, name: `name` });
    const legalName = useWatch({ control, name: `legalName` });
    const month = useWatch({ control, name: `month` });
    const day = useWatch({ control, name: `day` });

    function onSubmit(): void {
        if (!isDirty) {
            return;
        }

        if (getValues().type !== entity.type) {
            setShowEntityTypeConfirm(true);
            return;
        }
        updateEntity();
    }

    function updateEntity(): void {
        const { name, legalName, type, day, month, group } = getValues();

        const changedFields = {
            name: name === entity.name ? undefined : name,
            legalName: legalName === entity.legalName ? undefined : legalName,
            type: type === entity.type ? undefined : type,
            financialYearEndMonth: month === entity.financialYearEndMonth ? undefined : (month as number),
            financialYearEndDay: day === entity.financialYearEndDay ? undefined : (day as number),
            groups: (entity.groups.length === 0 && !group) || entity.groups[0]?.id === group ? undefined : group ? [group] : [],
        };

        if (Object.keys(changedFields).length === 0 || Object.values(changedFields).every((val) => !val)) {
            return;
        }

        updateEntitiesQuery.mutate([{ id: entity.id, fields: changedFields }], {
            onSuccess: () => {
                toastNotification('success', 'Entity details updated!');
            },
            onError: (e) => {
                const result = e.response?.data.results[0];
                setError('name', { message: result.error?.fields?.name });
                toastNotification('error', 'Unable to update entity details!');
            },
        });
    }

    return (
        <SettingsContainer>
            {showEntityTypeConfirm && (
                <EditEntityConfirm
                    onClose={(): void => setShowEntityTypeConfirm(false)}
                    onEdit={(): void => {
                        setShowEntityTypeConfirm(false);
                        updateEntity();
                    }}
                />
            )}
            <SectionLabel
                title="Details"
                description="Update your entity's information, such as its name, type, financial year end date, and group."
            />
            <form className="grid grid-cols-2 gap-8" onSubmit={handleSubmit(onSubmit)}>
                <div className="col-span-2">
                    <Controller
                        name="name"
                        rules={{ required: 'Entity name is required.' }}
                        control={control}
                        render={({ field: { onChange } }): JSX.Element => (
                            <InputV2
                                type="text"
                                title="Name"
                                value={name}
                                errorMessage={errors?.name?.message?.toString()}
                                onChange={(value): void => onChange(value)}
                                disabled={!activeSubscription}
                            />
                        )}
                    />
                </div>
                <div className="col-span-2">
                    <Controller
                        name="legalName"
                        rules={{ required: 'Entity legal name is required.' }}
                        control={control}
                        render={({ field: { onChange } }): JSX.Element => (
                            <InputV2
                                type="text"
                                title="Legal Name"
                                value={legalName}
                                errorMessage={errors?.name?.message?.toString()}
                                onChange={(value): void => onChange(value)}
                                disabled={!diffLegalName || !activeSubscription}
                            />
                        )}
                    />
                </div>
                <div className="flex gap-2 -mt-4">
                    <Checkbox
                        checked={diffLegalName}
                        onChange={(): void => setDiffLegalName(!diffLegalName)}
                        disabled={diffLegalName && name !== legalName}
                    />
                    <p className="text-gray-800 dark:text-gray-200 text-xs">Different legal name</p>
                </div>
                <div className="col-span-2">
                    <Controller
                        name="type"
                        rules={{ required: 'Entity type is required.' }}
                        control={control}
                        render={({ field: { onChange, value } }): JSX.Element => (
                            <EntityTypeSelect
                                size="small"
                                onChange={(options): void => onChange(options.length === 1 ? options[0] : null)}
                                defaultEntityType={value ? [value] : []}
                                title="Entity Type"
                                errorMessage={errors?.type?.message?.toString()}
                                isDisabled={!activeSubscription || entity.isArchived}
                            />
                        )}
                    />
                </div>
                <Controller
                    name="month"
                    rules={{ required: 'Financial year end month is required.' }}
                    control={control}
                    render={({ field: { onChange } }): JSX.Element => (
                        <MonthSelect
                            onChange={(options): void => {
                                onChange(options[0]);
                                setValue('day', null);
                            }}
                            size="small"
                            title="Financial year end month"
                            defaultValue={month || undefined}
                            errorMessage={errors?.month?.message?.toString()}
                            isDisabled={!activeSubscription || entity.isArchived}
                        />
                    )}
                />
                <Controller
                    name="day"
                    rules={{ required: 'Financial year end day is required.' }}
                    control={control}
                    render={({ field: { onChange } }): JSX.Element => (
                        <DaySelect
                            onChange={(options): void => onChange(options[0])}
                            size="small"
                            title="Financial year end day"
                            defaultValue={day || undefined}
                            selectedMonth={month || undefined}
                            errorMessage={errors?.day?.message?.toString()}
                            isDisabled={!activeSubscription || entity.isArchived}
                        />
                    )}
                />
                <div className="col-span-2">
                    <Controller
                        name="group"
                        control={control}
                        render={({ field: { onChange, value } }): JSX.Element => (
                            <GroupSelect
                                size="small"
                                onChange={(options): void => onChange(options.length === 0 ? null : options[0])}
                                title="Group"
                                defaultValue={value || undefined}
                                isDisabled={!activeSubscription || entity.isArchived}
                            />
                        )}
                    />
                </div>
                <div>
                    <ButtonV2 label="Save" size="sm" leftIcon={<CheckIcon />} isSubmit />
                </div>
            </form>
        </SettingsContainer>
    );
}

export default EntityDetails;
